/*
  ADA / WCAG 2.2.2 (2026-10-08): a visible pause / play control for every
  autoplaying Flickity slider that renders snippets/ada-autoplay-toggle.liquid
  (announcement bar, press logos, homepage before/after; the hero gets one the
  day it becomes a slideshow).

  How it works
  - The button is rendered hidden. Once Flickity has initialised the slider
    inside the same data-ada-autoplay-scope element and that slider was
    configured with autoPlay, the button is shown and wired up.
  - Pause = Flickity stopPlayer() with options.autoPlay cleared so nothing
    restarts it (same trick as ada-reduced-motion.js). Play restores the
    configured autoPlay value and calls playPlayer().
  - Flickity already stops autoplay for good on any user interaction (dot
    click, drag, arrows). The player's play()/stop() are wrapped so the button
    always reflects the real state, whoever changed it. Hover-pause
    (pauseAutoPlayOnHover) is a 'paused' state and still counts as playing.
  - With prefers-reduced-motion the button is never shown: autoplay is off by
    the visitor's own choice and ada-reduced-motion.js would stop it again.
  - placement="dots" moves the button into the slider's page-dot row.
  - The scope element gets .ada-autoplay-scope--active once a button is shown,
    so the CSS that makes room for the button (announcement text padding, press
    logo gutter) applies only then.

  PDP gallery videos (product-media-gallery-v2 / product-thumbnail-v2) autoplay
  muted and looped with the native controls switched off, so they get the same
  button: it is cloned from the <template data-ada-video-toggle-template> the
  gallery renders, dropped into the video's deferred-media box (bottom-left,
  per the designer's PDP mockup) the moment the video loads or starts, and
  follows the element's own play / pause events. Videos that carry native
  controls (the zoom modal) are left alone. This part also runs under
  prefers-reduced-motion: ada-reduced-motion.js stops the autoplay there, and
  the button is how the visitor starts the video on purpose (it marks the
  video data-user-played so that script does not pause it again).

  Other autoplaying videos (2026-10-21, 216digital remaining-issues list: the
  Meet the Experts CGI loop, the Wildling how-to-use animation that replaced a
  GIF) render the snippet themselves with mode: 'video', right after the
  <video> inside a positioned wrapper. Such a button carries
  data-ada-video-button; it is wired to the <video> that shares its parent and
  behaves exactly like the gallery one.
*/
(function () {
  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- autoplaying <video> elements ---------- */
  function wireVideo(video, btn, box) {
    var label = btn.getAttribute('data-label') || 'video';
    video.setAttribute('data-ada-video-toggle', '');

    function sync() {
      var playing = !video.paused && !video.ended;
      btn.classList.toggle('is-playing', playing);
      btn.setAttribute('aria-label', (playing ? 'Pause ' : 'Play ') + label);
    }
    btn.addEventListener('click', function () {
      if (video.paused || video.ended) {
        video.setAttribute('data-user-played', '');
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      } else {
        video.pause();
      }
    });
    ['play', 'playing', 'pause', 'ended', 'emptied'].forEach(function (type) {
      video.addEventListener(type, sync);
    });
    if (btn.parentNode !== box) box.appendChild(btn);
    btn.hidden = false;
    sync();
  }
  // PDP gallery: the button is cloned from the gallery's template.
  function setupVideo(video) {
    if (video.hasAttribute('data-ada-video-toggle') || video.controls) return;
    var host = video.closest('.product-media-container');
    if (!host) return;
    var template = document.querySelector('[data-ada-video-toggle-template]');
    if (!template || !template.content) return;
    var box = video.closest('deferred-media') || host;
    wireVideo(video, template.content.firstElementChild.cloneNode(true), box);
  }
  // Sections: the button is already in the markup, next to its <video>.
  function setupVideoButton(btn) {
    if (btn.hasAttribute('data-ada-video-ready')) return;
    var box = btn.parentElement;
    var video = box && box.querySelector('video');
    if (!video || video.controls || video.hasAttribute('data-ada-video-toggle')) return;
    btn.setAttribute('data-ada-video-ready', '');
    wireVideo(video, btn, box);
  }
  function onVideoEvent(e) {
    var v = e.target;
    if (v && v.tagName === 'VIDEO') setupVideo(v);
  }
  document.addEventListener('loadedmetadata', onVideoEvent, true);
  document.addEventListener('play', onVideoEvent, true);
  // The gallery appends the <video> when its slide is selected; attach as soon
  // as it is in the DOM so the button is there even before any media loads
  // (background tab, data saver, autoplay blocked).
  function scanVideos(root) {
    var videos = (root && root.querySelectorAll) ? root.querySelectorAll('.product-media-container video') : [];
    for (var i = 0; i < videos.length; i++) setupVideo(videos[i]);
    if (root && root.tagName === 'VIDEO') setupVideo(root);
    var buttons = (root && root.querySelectorAll) ? root.querySelectorAll('[data-ada-video-button]') : [];
    for (var j = 0; j < buttons.length; j++) setupVideoButton(buttons[j]);
  }
  if (window.MutationObserver && document.querySelector('[data-ada-video-toggle-template], [data-ada-video-button]')) {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1) scanVideos(added[j]);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { scanVideos(document); });
  else scanVideos(document);
  document.addEventListener('shopify:section:load', function (e) { scanVideos(e.target || document); });

  /* ---------- Flickity sliders ---------- */
  if (REDUCED) return;

  function flickityOf(el) {
    try { return window.Flickity && window.Flickity.data(el); } catch (e) { return null; }
  }

  function setup(btn) {
    var scope = btn.closest('[data-ada-autoplay-scope]');
    var carousel = scope && scope.querySelector('.flickity-enabled');
    var flkty = carousel && flickityOf(carousel);
    if (!flkty || !flkty.player) return false;

    btn.setAttribute('data-ada-autoplay-ready', '');
    var configured = flkty.options.autoPlay;
    if (!configured) return true; // slider does not autoplay: nothing to control

    var label = btn.getAttribute('data-label') || 'slideshow';
    function sync() {
      var playing = flkty.player.state !== 'stopped';
      btn.classList.toggle('is-playing', playing);
      btn.setAttribute('aria-label', (playing ? 'Pause ' : 'Play ') + label);
    }

    ['play', 'stop'].forEach(function (method) {
      var original = flkty.player[method];
      flkty.player[method] = function () {
        var result = original.apply(this, arguments);
        sync();
        return result;
      };
    });

    btn.addEventListener('click', function () {
      if (flkty.player.state !== 'stopped') {
        flkty.options.autoPlay = false;
        flkty.stopPlayer();
      } else {
        flkty.options.autoPlay = configured;
        flkty.playPlayer();
      }
      sync();
    });

    if (btn.getAttribute('data-ada-autoplay-placement') === 'dots') {
      var dots = carousel.querySelector('.flickity-page-dots');
      if (dots) {
        var item = document.createElement('li');
        item.className = 'ada-autoplay-toggle__item';
        item.appendChild(btn);
        dots.appendChild(item);
      }
    }

    btn.hidden = false;
    scope.classList.add('ada-autoplay-scope--active'); // CSS makes room for the button only when it is shown
    sync();
    return true;
  }

  var observer = null;
  var pending = null;

  function scan() {
    pending = null;
    var buttons = document.querySelectorAll('[data-ada-autoplay-toggle]:not([data-ada-autoplay-ready])');
    var remaining = 0;
    for (var i = 0; i < buttons.length; i++) {
      if (!setup(buttons[i])) remaining++;
    }
    if (!remaining && observer) { observer.disconnect(); observer = null; }
  }
  function scanSoon() {
    if (pending) return;
    pending = setTimeout(scan, 250);
  }

  function start() {
    scan();
    // Flickity initialises from data-flickity on its own DOMContentLoaded tick,
    // and sections can render late (theme editor, lazy sections).
    if (window.MutationObserver && document.querySelector('[data-ada-autoplay-toggle]:not([data-ada-autoplay-ready])')) {
      observer = new MutationObserver(scanSoon);
      observer.observe(document.documentElement, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['class']
      });
      setTimeout(function () { if (observer) { observer.disconnect(); observer = null; } }, 15000);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  window.addEventListener('load', scanSoon);
  document.addEventListener('shopify:section:load', function () { if (!observer) start(); else scanSoon(); });
})();
